import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { lcApiBase, lcHttpError } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface CaisseClient {
  id: number;
  user_id: number;
  station_id: number;
  nom?: string | null;
  prenom?: string | null;
  email?: string | null;
  telephone?: string | null;
  point_fidelite?: number | null;
  carte_fidelite?: string | null;
  date_naissance?: string | null;
  type?: 'Individuel' | 'Professionnel' | 'Négoce' | null;
  civilite?: 'Homme' | 'Femme' | null;
  identifiant_unique?: string | null;
  nom_entreprise?: string | null;
  numero_identification_fiscale?: string | null;
  code_client?: string | null;
  raison_social?: string | null;
  forme_juridique?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  [key: string]: unknown;
}

export interface CreateCaisseClientPayload {
  nom: string;
  prenom?: string;
  email?: string;
  telephone?: string;
  carte_fidelite?: string;
  date_naissance?: string;
  point_fidelite?: number;
  type?: 'Individuel' | 'Professionnel' | 'Négoce';
  civilite?: 'Homme' | 'Femme';
  identifiant_unique?: string;
  nom_entreprise?: string;
  numero_identification_fiscale?: string;
  code_client?: string;
  raison_social?: string;
  forme_juridique?: string;
}

export interface ClientsPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ClientsPageResult {
  items: CaisseClient[];
  pagination: ClientsPaginationMeta;
}

interface ClientsListResponse {
  success: boolean;
  data?: CaisseClient[];
  pagination?: ClientsPaginationMeta;
  error?: string;
}

interface CreateClientResponse {
  success: boolean;
  data?: CaisseClient;
  message?: string;
  error?: string;
}

interface UpdateClientResponse {
  success: boolean;
  data?: CaisseClient;
  message?: string;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseClientService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  updateClient(
    caisseClientId: number,
    payload: Partial<CreateCaisseClientPayload>
  ): Observable<CaisseClient> {
    if (!this.authService.getLogicielCaisseToken() || !this.authService.getBackStationId()) {
      return throwError(
        () => new Error('Session logiciel caisse incomplète : reconnectez-vous.')
      );
    }
    const id = Math.floor(Number(caisseClientId));
    if (!Number.isFinite(id) || id <= 0) {
      return throwError(() => new Error('Identifiant client caisse invalide.'));
    }

    const url = `${lcApiBase(this.authService)}/api/caisse/clients/${id}`;
    return this.http.put<UpdateClientResponse>(url, payload).pipe(
      map((res) => {
        if (!res?.success || !res.data) {
          throw new Error(res?.error || 'Réponse de mise à jour client invalide');
        }
        return res.data;
      }),
      catchError((err: unknown) => {
        if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
          return throwError(() => err);
        }
        return throwError(() =>
          lcHttpError(err as HttpErrorResponse, 'Erreur lors de la mise à jour du client caisse.')
        );
      })
    );
  }

  createClient(payload: CreateCaisseClientPayload): Observable<CaisseClient> {
    if (!this.authService.getLogicielCaisseToken() || !this.authService.getBackStationId()) {
      return throwError(
        () => new Error('Session logiciel caisse incomplète : reconnectez-vous.')
      );
    }

    const url = `${lcApiBase(this.authService)}/api/caisse/clients`;
    return this.http.post<CreateClientResponse>(url, payload).pipe(
      map((res) => {
        if (!res?.success || !res.data) {
          throw new Error(res?.error || 'Réponse de création client invalide');
        }
        return res.data;
      }),
      catchError((err: unknown) => {
        if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
          return throwError(() => err);
        }
        return throwError(() =>
          lcHttpError(err as HttpErrorResponse, 'Erreur lors de la création du client caisse.')
        );
      })
    );
  }

  getClients(options?: {
    search?: string;
    page?: number;
    limit?: number;
    /** Si omis : station back session logiciel caisse. */
    stationId?: number;
  }): Observable<ClientsPageResult> {
    const stationId =
      options?.stationId != null && Number.isFinite(Number(options.stationId))
        ? Number(options.stationId)
        : this.authService.getBackStationId();
    if (stationId == null || !Number.isFinite(stationId) || stationId <= 0) {
      return throwError(
        () => new Error('Session incomplète : reconnectez-vous (station caisse manquante).')
      );
    }
    if (!this.authService.getLogicielCaisseToken()) {
      return throwError(
        () => new Error('Session incomplète : token logiciel caisse manquant.')
      );
    }

    let params = new HttpParams();
    const page = options?.page ?? 1;
    const limit = options?.limit ?? 10;
    params = params.set('page', String(Math.max(1, page)));
    params = params.set('limit', String(Math.min(100, Math.max(1, limit))));
    if (options?.search?.trim()) {
      params = params.set('search', options.search.trim());
    }

    const url = `${lcApiBase(this.authService)}/api/caisse/clients/${stationId}`;
    return this.http.get<ClientsListResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.data)) {
          throw new Error(res?.error || 'Réponse clients invalide');
        }
        const p = res.pagination;
        const pagination: ClientsPaginationMeta = p
          ? {
              page: p.page,
              limit: p.limit,
              total: p.total,
              totalPages: p.totalPages,
            }
          : {
              page: 1,
              limit: limit,
              total: res.data.length,
              totalPages: 1,
            };
        return { items: res.data, pagination };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des clients.'))
      )
    );
  }

  mapClientToTableRow(c: CaisseClient): Record<string, string> {
    const nom = [c.prenom, c.nom].filter(Boolean).join(' ').trim() || '—';
    const pts = c.point_fidelite ?? 0;
    return {
      id: String(c.id),
      CLIENT: nom,
      MAIL: c.email ?? '—',
      'NUMÉRO DE TÉLÉPHONE': c.telephone ?? '—',
      POINTS: `${pts} pts`,
    };
  }
}
